import { useCallback, useEffect, useRef, useState } from 'react';
import type { UploadableImageAsset } from '@/lib/image-upload';
import type { PhotoKind } from '@/lib/photo-crop-geometry';
import type { PhotoSource } from './photo-types';
import { releasePhotoAsset, selectPhotoSource } from './photo-source';
import { PhotoCropModal } from './PhotoCropModal';
export { releasePhotoAsset } from './photo-source';

/** Selection/cropping only. The caller persists to the explicit account or CV owner. */
export function usePhotoPicker({ kind, previewShape }: { kind: PhotoKind; previewShape?: 'round' | 'square' }) {
  const [source, setSource] = useState<PhotoSource | null>(null);
  const [isPicking, setPicking] = useState(false);
  const active = useRef<AbortController | null>(null);
  const currentSource = useRef<PhotoSource | null>(null);
  const resolveCrop = useRef<((asset: UploadableImageAsset | null) => void) | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      active.current?.abort();
      currentSource.current?.release();
      currentSource.current = null;
      resolveCrop.current?.(null);
      resolveCrop.current = null;
    };
  }, []);

  const finish = useCallback((asset: UploadableImageAsset | null) => {
    if (!resolveCrop.current) {
      if (asset) void releasePhotoAsset(asset);
      return;
    }
    resolveCrop.current?.(asset);
    resolveCrop.current = null;
    currentSource.current?.release();
    currentSource.current = null;
    if (mounted.current) setSource(null);
  }, []);

  const pickPhoto = useCallback(async (): Promise<UploadableImageAsset | null> => {
    if (active.current || !mounted.current) return null;
    const controller = new AbortController();
    active.current = controller;
    setPicking(true);
    try {
      const selected = await selectPhotoSource(controller.signal);
      if (!selected) return null;
      if (!mounted.current || controller.signal.aborted) { selected.release(); return null; }
      currentSource.current = selected;
      return await new Promise<UploadableImageAsset | null>(resolve => {
        resolveCrop.current = resolve;
        setSource(selected);
      });
    } catch (error) {
      if (controller.signal.aborted || !mounted.current) return null;
      throw error;
    } finally {
      active.current = null;
      if (mounted.current) setPicking(false);
    }
  }, []);

  return {
    pickPhoto,
    isPicking,
    cropper: source ? <PhotoCropModal key={source.asset.uri} source={source} kind={kind} previewShape={previewShape} onCancel={() => finish(null)} onConfirm={finish} /> : null,
  };
}
